import {
  createSalesCycleRequestSchema,
  toFieldIssues,
  type ApiError,
  type CreateSalesCycleRequest,
  type FieldIssue,
  type Finding,
  type SalesCycleImpactItem,
  type ValidationFailedError,
} from '@complan/contracts';

/**
 * The pure, framework-free logic behind the Sales_Cycle_Timing_Form and its
 * impact list (task 23.3).
 *
 * Everything a person reads is a component concern and lives in the catalogue;
 * everything a *rule* decides lives here as plain functions over plain values, so
 * the two behaviours the task singles out are testable without a DOM:
 *
 * 1. **The default-selection rule** ({@link defaultReassignSelection}). R6.10: an
 *    Initiative that carries a manual override or holds status Approved or Final
 *    is excluded from the *default* reassignment selection and must be chosen
 *    individually. The impact endpoint has already made that call per row — it
 *    sets `requiresExplicitSelection` — so the client never re-derives the rule
 *    from status or source; it honours the flag. Keeping this a pure function is
 *    what lets a test prove "override / Approved / Final never appear in the
 *    default selection" over any impact list.
 *
 * 2. **Error-retains-values classification** ({@link classifyWriteError}). R6.2
 *    (duplicate name) and R6.3 (inverted range) are *hard* blocks: the save is
 *    rejected and the entered values must survive so the user can correct them.
 *    R6.4 (overlap) and R6.5 (gap) are *warnings*: nothing persists until the
 *    user acknowledges, and the acknowledgement is a resubmission carrying the
 *    warning rule ids. Both arrive as one `validation_failed` shape distinguished
 *    only by the *tier* of the findings it carries, so classification is a pure
 *    read over the finding set — and the form never clears itself on either
 *    outcome, because neither branch resets the entered values.
 */

/** The wire spelling of the assignment source (R7.3, R7.4); mirrors the contract enum. */
export type AssignmentSource = SalesCycleImpactItem['currentAssignmentSource'];

/**
 * The form's own field state (task 23.3, R6.1).
 *
 * Dates are held as the raw `YYYY-MM-DD` strings an `<input type="date">`
 * produces (empty while unset), *not* as the branded `PlainDate` the wire uses:
 * the form is where unvalidated text lives, and branding happens at the boundary
 * when the request is built ({@link toWriteRequest}), so an empty or malformed
 * date surfaces as a field issue rather than being asserted valid at the input.
 */
export interface SalesCycleFormValues {
  readonly planningYear: number;
  readonly name: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly isActive: boolean;
}

/** A blank definition seeded with a planning year. */
export function emptyFormValues(planningYear: number): SalesCycleFormValues {
  return {
    planningYear,
    name: '',
    startDate: '',
    endDate: '',
    isActive: true,
  };
}

/**
 * The default set of Initiative ids selected for reassignment (R6.10).
 *
 * Exactly the affected Initiatives that do *not* require an explicit selection:
 * an Initiative with `requiresExplicitSelection === true` — a manual override, or
 * status Approved or Final — is left out and must be ticked by hand. The result
 * preserves the input order and is a fresh array, so a caller can seed selection
 * state from it without sharing a reference.
 */
export function defaultReassignSelection(
  items: readonly SalesCycleImpactItem[],
): readonly string[] {
  return items
    .filter((item) => !item.requiresExplicitSelection)
    .map((item) => item.initiativeId);
}

/**
 * Whether an impact row may be selected as part of the *default* selection.
 *
 * The inverse of `requiresExplicitSelection`: a row that requires an explicit
 * selection is still selectable, just never selected by default. Split out so the
 * table and {@link defaultReassignSelection} agree on the same predicate.
 */
export function isDefaultSelectable(item: SalesCycleImpactItem): boolean {
  return !item.requiresExplicitSelection;
}

/** The result of building a wire request from the form's field state. */
export type BuildRequestResult =
  | { readonly ok: true; readonly request: CreateSalesCycleRequest }
  | { readonly ok: false; readonly fieldIssues: readonly FieldIssue[] };

/**
 * Builds the `POST`/`PUT` request body from the form's field state (R6.1).
 *
 * The raw `YYYY-MM-DD` date strings and the acknowledged warning rule ids are
 * validated through the same `@complan/contracts` schema Fastify uses, so a
 * malformed or empty date is rejected here — as field issues addressing the
 * offending field — before a request is sent, rather than round-tripping to be
 * rejected server-side. On success the dates are the branded `PlainDate` the wire
 * expects.
 */
export function toWriteRequest(
  values: SalesCycleFormValues,
  acknowledgedWarningRuleIds: readonly string[],
): BuildRequestResult {
  const parsed = createSalesCycleRequestSchema.safeParse({
    planningYear: values.planningYear,
    name: values.name,
    startDate: values.startDate,
    endDate: values.endDate,
    // R16.8 requires an effective-from date on every Sales_Cycle record, and the
    // form collects no separate control for it: a cycle definition takes effect
    // when the cycle itself begins, so the start date *is* the effective-from
    // here. Derived rather than held as a form field so there is no fourth date
    // a user could set inconsistently with the other three — and, like the rest
    // of them, it is validated by the schema below rather than asserted here.
    effectiveFrom: values.startDate,
    isActive: values.isActive,
    acknowledgedWarningRuleIds: [...acknowledgedWarningRuleIds],
  });
  if (parsed.success) {
    return { ok: true, request: parsed.data };
  }
  return { ok: false, fieldIssues: toFieldIssues(parsed.error) };
}

/** The four outcomes a Sales_Cycle write can produce, as the client sees them. */
export type WriteErrorClassification =
  | {
      /** A hard block (R6.2 duplicate name, R6.3 inverted range): entered values are retained. */
      readonly kind: 'blocked';
      /** The tier-`hard` findings, each already carrying its message key and detail. */
      readonly findings: readonly Finding[];
      /** The union of field paths the findings name, for highlighting. */
      readonly fields: readonly string[];
    }
  | {
      /** A warning that must be acknowledged (R6.4 overlap, R6.5 gap) before anything persists. */
      readonly kind: 'acknowledgement_required';
      /** The tier-`warning` findings, each carrying its affected cycles/ranges in `detail`. */
      readonly findings: readonly Finding[];
      /** The rule ids to echo back in `acknowledgedWarningRuleIds` on resubmission. */
      readonly acknowledgeableRuleIds: readonly string[];
    }
  | {
      /** A stale impact preview after a serialization failure (R6.7–R6.9): re-preview. */
      readonly kind: 'stale_preview';
    }
  | {
      /** Anything else — an unexpected shape the form surfaces generically. */
      readonly kind: 'other';
    };

/** True when the finding is a hard block; a hard finding is never acknowledgeable. */
function isHard(finding: Finding): boolean {
  return finding.tier === 'hard';
}

/** True when the finding is an acknowledgeable warning (R6.4, R6.5). */
function isWarning(finding: Finding): boolean {
  return finding.tier === 'warning';
}

/** Distinct values, order preserved. */
function distinct(values: readonly string[]): readonly string[] {
  return [...new Set(values)];
}

/**
 * Classifies a failed Sales_Cycle write into how the form must react (R6.2–R6.5,
 * R6.7–R6.9).
 *
 * The rules, in priority order:
 * - a `conflict` of kind `stale_preview` means the impact list must be re-run;
 * - a `validation_failed` carrying any tier-`hard` finding is a **block**: report
 *   the findings, name their fields, and retain the entered values;
 * - a `validation_failed` carrying only tier-`warning` findings is an
 *   **acknowledgement**: report them and collect their rule ids to resubmit;
 * - anything else is `other`.
 *
 * Crucially, no branch instructs the caller to clear the form — retaining the
 * entered values on a rejected save (R6.2/R6.3) is the *absence* of a reset, which
 * the component honours by never resetting on this path.
 */
export function classifyWriteError(error: ApiError): WriteErrorClassification {
  if (error.code === 'conflict' && error.conflict.kind === 'stale_preview') {
    return { kind: 'stale_preview' };
  }

  if (error.code === 'validation_failed') {
    return classifyValidationFailure(error);
  }

  return { kind: 'other' };
}

/** The `validation_failed` branch of {@link classifyWriteError}. */
function classifyValidationFailure(error: ValidationFailedError): WriteErrorClassification {
  const hardFindings = error.findings.filter(isHard);
  if (hardFindings.length > 0) {
    const fieldIssueFields = error.fieldIssues.map((issue) => issue.path);
    const findingFields = hardFindings.flatMap((finding) => finding.fields);
    return {
      kind: 'blocked',
      findings: hardFindings,
      fields: distinct([...findingFields, ...fieldIssueFields]),
    };
  }

  const warningFindings = error.findings.filter(isWarning);
  if (warningFindings.length > 0) {
    return {
      kind: 'acknowledgement_required',
      findings: warningFindings,
      acknowledgeableRuleIds: distinct(warningFindings.map((finding) => finding.ruleId)),
    };
  }

  // A `validation_failed` with no hard and no warning finding is a request-shape
  // rejection (only `fieldIssues`): still a block that retains values, with no
  // findings to render beyond the field markers.
  if (error.fieldIssues.length > 0) {
    return {
      kind: 'blocked',
      findings: [],
      fields: distinct(error.fieldIssues.map((issue) => issue.path)),
    };
  }

  return { kind: 'other' };
}
